import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import Icon from '../components/ui/Icon';
import DropZone from '../components/tools/DropZone';
import { FadeInWhenVisible, HoverLift } from '../components/ui/Motion';
import { POPULAR_TOOLS, TOOL_CATEGORIES, INTEGRATIONS } from '../data/tools';
import { authActions } from '../store';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { user, isAuthenticated, freeDocsUsed, freeDocsLimit } = useSelector(s => s.auth);
  const { history } = useSelector(s => s.files);

  const quickActions = POPULAR_TOOLS.slice(0, 8);

  // Mock activity if empty
  const activityData = history.length > 0 ? history : [
    { id: '1', toolName: 'PDF to Word', fileName: 'contract.pdf', category: 'convertFrom', timestamp: Date.now() - 120000 },
    { id: '2', toolName: 'Compress PDF', fileName: 'report.pdf', category: 'optimize', timestamp: Date.now() - 3600000 },
    { id: '3', toolName: 'Remove Background', fileName: 'photo.jpg', category: 'image', timestamp: Date.now() - 7200000 },
    { id: '4', toolName: 'Merge PDF', fileName: 'slides.pdf', category: 'organize', timestamp: Date.now() - 18000000 },
    { id: '5', toolName: 'Sign PDF', fileName: 'agreement.pdf', category: 'security', timestamp: Date.now() - 36000000 },
  ];

  const timeAgo = (ts) => {
    const d = Date.now() - ts;
    if (d < 60000) return 'Just now';
    if (d < 3600000) return `${Math.floor(d / 60000)}m ago`;
    if (d < 86400000) return `${Math.floor(d / 3600000)}h ago`;
    return `${Math.floor(d / 86400000)}d ago`;
  };

  return (
    <div className="min-h-screen pt-24 pb-16" style={{ background: 'var(--bg-secondary)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Welcome back, {user?.name || 'there'} 👋
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              Here's your document workspace overview.
            </p>
          </div>
          <Link to="/pricing" className="btn-primary !py-2.5 !text-sm inline-flex items-center gap-2">
            <Icon name="Zap" size={14} /> Upgrade to Pro
          </Link>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Upload */}
            <FadeInWhenVisible>
              <div className="card p-6">
                <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Icon name="Upload" size={16} style={{ color: '#3374ff' }} />
                  Quick Upload
                </h3>
                <DropZone compact />
              </div>
            </FadeInWhenVisible>

            {/* Quick Actions */}
            <FadeInWhenVisible delay={0.1}>
              <div className="card p-6">
                <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Icon name="Zap" size={16} style={{ color: '#f9603e' }} />
                  Quick Actions
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {quickActions.map(tool => {
                    const cat = TOOL_CATEGORIES[tool.category];
                    return (
                      <Link key={tool.id} to={`/tool/${tool.id}`}>
                        <HoverLift lift={-3}>
                          <div className="p-4 rounded-xl text-center transition-colors"
                            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
                            <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center"
                              style={{ background: cat.bgLight, color: cat.color }}>
                              <Icon name={tool.icon} size={18} />
                            </div>
                            <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                              {tool.name}
                            </p>
                          </div>
                        </HoverLift>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </FadeInWhenVisible>

            {/* Recent Activity */}
            <FadeInWhenVisible delay={0.2}>
              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <Icon name="Clock" size={16} style={{ color: '#8b5cf6' }} />
                    Recent Activity
                  </h3>
                  <button className="text-xs font-medium" style={{ color: '#3374ff' }}>View all</button>
                </div>
                <div className="space-y-1">
                  {activityData.slice(0, 5).map((item, i) => {
                    const cat = TOOL_CATEGORIES[item.category];
                    return (
                      <motion.div key={item.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-black/[0.02] transition-colors">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: cat?.bgLight, color: cat?.color }}>
                          <Icon name="FileText" size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                            {item.toolName}
                          </p>
                          <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                            {item.fileName}
                          </p>
                        </div>
                        <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                          {timeAgo(item.timestamp)}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </FadeInWhenVisible>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Usage Stats */}
            <FadeInWhenVisible delay={0.1}>
              <div className="card p-6">
                <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Icon name="Star" size={16} style={{ color: '#f59e0b' }} />
                  Usage This Month
                </h3>
                <div className="text-center mb-4">
                  <div className="relative w-32 h-32 mx-auto">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="var(--bg-tertiary)" strokeWidth="8" />
                      <circle cx="50" cy="50" r="40" fill="none"
                        stroke={isAuthenticated ? '#10b981' : '#3374ff'}
                        strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={`${(freeDocsUsed / freeDocsLimit) * 251.2} 251.2`} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                        {isAuthenticated ? '∞' : `${freeDocsLimit - freeDocsUsed}`}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {isAuthenticated ? 'Unlimited' : 'remaining'}
                      </span>
                    </div>
                  </div>
                </div>
                {!isAuthenticated && (
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                      <span>{freeDocsUsed} used</span>
                      <span>{freeDocsLimit} total</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-tertiary)' }}>
                      <div className="h-full rounded-full" style={{
                        width: `${(freeDocsUsed / freeDocsLimit) * 100}%`,
                        background: 'linear-gradient(90deg, #3374ff, #8b5cf6)',
                      }} />
                    </div>
                    <button onClick={() => dispatch(authActions.setShowAuthModal(true))}
                      className="btn-primary w-full !py-2.5 !text-sm">
                      Unlock Unlimited
                    </button>
                  </div>
                )}
              </div>
            </FadeInWhenVisible>

            {/* Cloud Storage */}
            <FadeInWhenVisible delay={0.2}>
              <div className="card p-6">
                <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Icon name="Cloud" size={16} style={{ color: '#06b6d4' }} />
                  Cloud Storage
                </h3>
                <div className="space-y-2">
                  {INTEGRATIONS.slice(0, 4).map(int => (
                    <button key={int.id}
                      className="w-full flex items-center gap-3 p-3 rounded-xl transition-colors hover:bg-black/[0.02]"
                      style={{ border: '1px solid var(--border-light)' }}>
                      <span className="text-xl">{int.icon}</span>
                      <span className="flex-1 text-sm font-medium text-left" style={{ color: 'var(--text-primary)' }}>
                        {int.name}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>
                        Connect
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </FadeInWhenVisible>

            {/* Pro Features */}
            <FadeInWhenVisible delay={0.3}>
              <div className="rounded-2xl p-6 relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #0f1225, #1a1f3a)' }}>
                <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-20 -translate-y-1/2 translate-x-1/2"
                  style={{ background: '#3374ff' }} />
                <div className="relative z-10">
                  <span className="text-2xl mb-3 block">⚡</span>
                  <h4 className="text-base font-bold text-white mb-2">Unlock Pro Features</h4>
                  <ul className="space-y-2 mb-4">
                    {['Unlimited conversions', 'Batch processing', 'OCR technology', 'Priority support'].map(f => (
                      <li key={f} className="flex items-center gap-2 text-xs text-white/70">
                        <Icon name="Check" size={12} className="text-green-400" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/pricing" className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-white/80 transition-colors">
                    View Plans <Icon name="ArrowRight" size={14} />
                  </Link>
                </div>
              </div>
            </FadeInWhenVisible>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
